import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  IntellioAssistantRunRecord,
  IntellioAssistantWorkItemRecord,
} from '@triedge/domain-core';
import type { AssistantRealtimeBridgeEvent } from '../contracts/assistant-realtime-contracts';

export type AssistantRealtimeConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export type AssistantRealtimeRunState = {
  runId: string;
  state: string;
  module: string;
  route: string;
  workItemId?: string | null;
  origin?: {
    sessionId?: string | null;
    threadId?: string | null;
    userMessageId?: string | null;
    assistantMessageId?: string | null;
  } | null;
  answer: string;
  response?: Record<string, unknown> | null;
  errorMessage?: string | null;
  updatedAtEpochMs: number;
};

export type AssistantRealtimeState = {
  connectionStatus: AssistantRealtimeConnectionStatus;
  lastErrorMessage: string | null;
  lastEventAtEpochMs: number | null;
  seenEventIds: Record<string, true>;
  queuedWorkItems: IntellioAssistantWorkItemRecord[];
  latestWorkItemsById: Record<string, IntellioAssistantWorkItemRecord>;
  runsById: Record<string, AssistantRealtimeRunState>;
};

const initialState: AssistantRealtimeState = {
  connectionStatus: 'idle',
  lastErrorMessage: null,
  lastEventAtEpochMs: null,
  seenEventIds: {},
  queuedWorkItems: [],
  latestWorkItemsById: {},
  runsById: {},
};

const upsertWorkQueue = (queue: IntellioAssistantWorkItemRecord[], item: IntellioAssistantWorkItemRecord) => {
  const existingIndex = queue.findIndex((entry) => entry.id === item.id);
  if (['dismissed', 'completed', 'failed', 'cancelled', 'expired'].includes(item.state)) {
    if (existingIndex >= 0) {
      queue.splice(existingIndex, 1);
    }
    return;
  }
  if (existingIndex >= 0) {
    queue[existingIndex] = item;
    return;
  }
  queue.unshift(item);
};

const upsertLatestWorkItem = (
  latestWorkItemsById: Record<string, IntellioAssistantWorkItemRecord>,
  item: IntellioAssistantWorkItemRecord,
) => {
  latestWorkItemsById[item.id] = item;
};

const upsertRunState = (
  runsById: Record<string, AssistantRealtimeRunState>,
  run: IntellioAssistantRunRecord,
  response?: Record<string, unknown> | null,
  updatedAtEpochMs?: number,
) => {
  runsById[run.id] = {
    runId: run.id,
    state: run.state,
    module: run.module,
    route: run.route,
    workItemId: run.workItemId ?? null,
    origin: run.origin ?? null,
    answer: run.answer ?? '',
    response: response ?? null,
    errorMessage: run.errorMessage ?? null,
    updatedAtEpochMs: updatedAtEpochMs ?? (Number(run.updatedAt) * 1000),
  };
};

export const assistantRealtimeSlice = createSlice({
  name: 'assistantRealtime',
  initialState,
  reducers: {
    resetAssistantRealtimeState: () => initialState,

    sessionHydrated: (
      state,
      action: PayloadAction<{
        workItems?: readonly IntellioAssistantWorkItemRecord[];
        activeRun?: IntellioAssistantRunRecord | null;
        response?: Record<string, unknown> | null;
        nowEpochMs: number;
      }>
    ) => {
      state.lastEventAtEpochMs = action.payload.nowEpochMs;
      state.queuedWorkItems = Array.isArray(action.payload.workItems)
        ? [...action.payload.workItems]
        : [];
      state.latestWorkItemsById = Array.isArray(action.payload.workItems)
        ? Object.fromEntries(action.payload.workItems.map((item) => [item.id, item] as const))
        : {};
      if (action.payload.activeRun) {
        upsertRunState(
          state.runsById,
          action.payload.activeRun,
          action.payload.response ?? null,
          action.payload.nowEpochMs,
        );
      }
    },

    connectionStateChanged: (
      state,
      action: PayloadAction<{
        status: AssistantRealtimeConnectionStatus;
        errorMessage?: string | null;
        nowEpochMs: number;
      }>
    ) => {
      state.connectionStatus = action.payload.status;
      state.lastErrorMessage = action.payload.errorMessage ?? null;
      state.lastEventAtEpochMs = action.payload.nowEpochMs;
    },

    eventReceived: (state, action: PayloadAction<AssistantRealtimeBridgeEvent>) => {
      const eventId = String(action.payload.event.id ?? '').trim();
      if (!eventId || state.seenEventIds[eventId]) {
        return;
      }
      state.seenEventIds[eventId] = true;
      state.lastEventAtEpochMs = action.payload.receivedAtEpochMs;

      const kind = action.payload.event.kind;
      if (action.payload.workItem) {
        upsertWorkQueue(
          state.queuedWorkItems as unknown as IntellioAssistantWorkItemRecord[],
          action.payload.workItem,
        );
        upsertLatestWorkItem(
          state.latestWorkItemsById as unknown as Record<string, IntellioAssistantWorkItemRecord>,
          action.payload.workItem,
        );
      }
      if (action.payload.run) {
        upsertRunState(
          state.runsById,
          action.payload.run.run,
          action.payload.run.response ?? null,
          action.payload.receivedAtEpochMs,
        );
      }
      if (kind === 'run.failed' && action.payload.event.runId && !action.payload.run) {
        const current = state.runsById[action.payload.event.runId];
        if (current) {
          current.state = 'failed';
          current.errorMessage = action.payload.event.summary ?? current.errorMessage ?? 'Assistant run failed.';
          current.updatedAtEpochMs = action.payload.receivedAtEpochMs;
        }
      }
    },

    dequeueWorkItem: (state, action: PayloadAction<{ workItemId: string }>) => {
      state.queuedWorkItems = state.queuedWorkItems.filter((entry) => entry.id !== action.payload.workItemId);
    },

    clearRunState: (state, action: PayloadAction<{ runId: string }>) => {
      delete state.runsById[action.payload.runId];
    },
  },
});

export const assistantRealtimeReducer = assistantRealtimeSlice.reducer;
export const assistantRealtimeActions = assistantRealtimeSlice.actions;

export const selectAssistantRealtimeState = <TRootState extends { assistantRealtime: AssistantRealtimeState }>(
  state: TRootState
): AssistantRealtimeState => state.assistantRealtime;

export const selectAssistantRealtimeQueue = <TRootState extends { assistantRealtime: AssistantRealtimeState }>(
  state: TRootState
): readonly IntellioAssistantWorkItemRecord[] => state.assistantRealtime.queuedWorkItems;

export const selectAssistantRealtimeLatestWorkItems = createSelector(
  [
    <TRootState extends { assistantRealtime: AssistantRealtimeState }>(state: TRootState) =>
      state.assistantRealtime.latestWorkItemsById,
  ],
  (latestWorkItemsById): readonly IntellioAssistantWorkItemRecord[] => Object.values(latestWorkItemsById),
);

export const selectAssistantRealtimeUnreadCount = <TRootState extends { assistantRealtime: AssistantRealtimeState }>(
  state: TRootState
): number => state.assistantRealtime.queuedWorkItems.length;

export const selectAssistantRealtimeRunById = <TRootState extends { assistantRealtime: AssistantRealtimeState }>(
  state: TRootState,
  runId: string,
): AssistantRealtimeRunState | undefined => state.assistantRealtime.runsById[runId];

export const hasSeenAssistantRealtimeEvent = (
  state: AssistantRealtimeState,
  input: { eventId: string }
): boolean => Boolean(state.seenEventIds[String(input.eventId ?? '').trim()]);
